import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Theme from "./Theme.js";
import IdGen from "./IdGen.js";
import Text from "./Text.js";
import Label from "./Label.js";
import S from './PropertyGrid.css';

/** 
 * React class that renders a pane of properties in a grid
 * Options on PropertyGrid include
 * @property {string} className css class to be applied to this component 
 * @property {boolean} inline adds inline properties to the pane 
 * @property {string} variant Theme variant name to be added to the component 
 * @property {boolean} inline If true, an inline-grid is used instead of a grid.
 * @property {boolean} vertical If true, an labels are placed above editors.
 * @property {int} columns The number of columns to use in the grei.
 * 
 * On the PropertyGrid.Item, the following are options
 * @property {String|Component} label The label to give the grid cell
 * @property {String} labelVariant The variant to use for the label if its a string
 * @property {Component} preLabel Components to put in front of the label
 * @property {Component} postLabel Components to put behind of the label
 * @property {String} icon the icon given to the Label
 * @property {Object} iconOpts the icon options given to the Label
 * @property {Object} imageOpts the image options given to the Label
 * @property {boolean} fillCell If true, combines the label and child into on line.
 * @property {boolean} fillCellPreLabel like fillCell but the label is placed in front of child
 * @property {boolean} fill If true, the item will get its own line and will Stretch
 * across all the columns
 * @property {boolean} inset If true, the child will get inset spaceing. great for sub editors
 * @property {boolean} newRow If true, the item will be places at the beginning of a new row.
 * @property {boolean} stretch If true, the child will be asked to stretch across its grid cell
 * @property {boolean} inline If true, an inline-grid is used instead of a grid.
 * @property {boolean} fillVerticalLabelSpace If true and the label is null and there are more
 * the one column, this will insert a dummy label to keep spacing 
 **/
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
class PropertyGrid extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    className: PropTypes.string,
    inline: PropTypes.bool,
    variant: PropTypes.string
  };
  static defaultProps = {
    className: null,
    inline: false,
    variant: null
  };
  render() {
    const theme = this.context;
    const {
      className,
      variant,
      inline,
      labelVariant,
      headerVariant = 'title',
      subheaderVariant = 'subtitle',
      columns = 1,
      children,
      domRef,
      vertical,
      ...rest
    } = theme.props('PropertyGrid', this.props);
    const cn = theme.classes('PropertyGrid', 'propertyGrid', variant, S.pane, inline && S.inline, className);
    let column = 0;
    const style = columns < 1 ? null : {
      '--propertyGrid-columns': columns
    };
    return /*#__PURE__*/_jsx("div", {
      style: style,
      className: classnames(vertical ? S.gridVertical : S.gridHorizontal, cn),
      ref: domRef,
      ...rest,
      children: React.Children.map(children, m => {
        if (m?.type === PropertyGrid.Item) {
          const c = column++;
          if (m.props.newRow || m.props.fill) {
            column = 0;
          }
          if (column >= columns) {
            column = 0;
          }
          return /*#__PURE__*/React.cloneElement(m, {
            labelVariant,
            vertical,
            column: c,
            columns
          });
        }
        column = 0;
        if (m?.type === PropertyGrid.Header) {
          return /*#__PURE__*/React.cloneElement(m, {
            headerVariant
          });
        }
        if (m?.type === PropertyGrid.Subheader) {
          return /*#__PURE__*/React.cloneElement(m, {
            subheaderVariant
          });
        }
        if (m?.type === PropertyGrid.Divider) {
          return m;
        }
        return /*#__PURE__*/_jsx("div", {
          className: S.cell,
          children: m
        });
      })
    });
  }
}
class Item extends React.Component {
  constructor(props) {
    super(props);
    this.id = IdGen.createId('pgitem');
  }
  render() {
    const {
      fill,
      fillCell,
      stretch,
      newRow,
      fillCellPreLabel,
      inset,
      /* column, */columns,
      label,
      icon,
      imageOpts,
      iconOpts,
      vertical,
      preLabel,
      postLabel,
      fillVerticalLabelSpace,
      labelVariant,
      children
    } = this.props;
    const cArr = React.Children.toArray(children);
    let child = cArr == null ? null : cArr[0];
    let id = this.id;
    if (child) {
      if (child.props.id == null) {
        child = /*#__PURE__*/React.cloneElement(child, {
          id
        });
      } else {
        id = child.props.id;
      }
    }
    let lbl = label;
    if (typeof label === 'string' || label == null) {
      lbl = /*#__PURE__*/_jsx(Label, {
        className: classnames(fill && S.fill),
        htmlFor: id,
        variant: labelVariant,
        icon: icon,
        iconOpts: iconOpts,
        imageOpts: imageOpts,
        children: vertical && columns > 1 && fillVerticalLabelSpace && label == null ? '\u00A0' : label
      });
    }
    const lcn = classnames(S.label, !vertical && newRow && S.newRow);
    if (preLabel && postLabel) {
      lbl = /*#__PURE__*/_jsxs("div", {
        className: lcn,
        children: [" ", preLabel, " ", lbl, " ", postLabel, " "]
      });
    } else if (preLabel) {
      lbl = /*#__PURE__*/_jsxs("div", {
        className: lcn,
        children: [" ", preLabel, " ", lbl, " "]
      });
    } else if (postLabel) {
      lbl = /*#__PURE__*/_jsxs("div", {
        className: lcn,
        children: [" ", lbl, " ", postLabel, " "]
      });
    } else {
      lbl = /*#__PURE__*/_jsxs("div", {
        className: lcn,
        children: [" ", lbl, " "]
      });
    }

    // occupy all the cell as a single item
    if (fillCell) {
      if (fillCellPreLabel) {
        return /*#__PURE__*/_jsxs("div", {
          className: S.fillCell,
          children: [lbl, child]
        });
      }
      return /*#__PURE__*/_jsxs("div", {
        className: S.fillCell,
        children: [child, lbl]
      });
    }

    // horizontal
    if (fill) {
      if (label) {
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [lbl, /*#__PURE__*/_jsx("div", {
            className: classnames(S.fill, inset && S.inset),
            children: child
          })]
        });
      }
      return /*#__PURE__*/_jsx("div", {
        className: classnames(S.fill, inset && S.inset),
        children: child
      });
    }
    if (vertical) {
      return /*#__PURE__*/_jsxs("div", {
        className: classnames(S.verticalCell, newRow && S.newRow, stretch && S.stretch),
        children: [lbl || /*#__PURE__*/_jsx("div", {}), child]
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [lbl || /*#__PURE__*/_jsx("div", {}), stretch ? /*#__PURE__*/_jsx("div", {
        className: S.stretch,
        children: child
      }) : child || /*#__PURE__*/_jsx("div", {})]
    });
  }
}
function Divider(props) {
  return /*#__PURE__*/_jsx("div", {
    className: S.divider
  });
}
function Header({
  title,
  headerVariant,
  ...rest
}) {
  return /*#__PURE__*/_jsx(Text, {
    variant: headerVariant,
    className: S.header,
    ...rest,
    children: title
  });
}
function Subheader({
  title,
  subheaderVariant,
  ...rest
}) {
  return /*#__PURE__*/_jsx(Text, {
    variant: subheaderVariant,
    className: S.subheader,
    ...rest,
    children: title
  });
}
PropertyGrid.Item = Item;
PropertyGrid.Header = Header;
PropertyGrid.Subheader = Subheader;
PropertyGrid.Divider = Divider;
export default PropertyGrid;